'use strict';

import {createContext, useContext, useState, ReactNode, useEffect, useEffectEvent, useRef} from 'react';
import AuthenticationEndpoint from '../../api/endpoint/AuthenticationEndpoint';

const SESSION_RETRY_INTERVAL_MS = 60_000;
const MAX_TIMER_DELAY_MS = 2_147_000_000;

interface User {
    username: string;
    user_role: string;
    session_expires_in?: number;
}

interface SessionContextType {
    user: User | null;
    setUser: (user: User | null) => void;
    loading: boolean;
    isLoggingOut: boolean;
    setIsLoggingOut: (isLoggingOut: boolean) => void;
    refreshSession: () => Promise<void>;
}

export const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider = ({children}: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    // F-06: Track the latest request ID to prevent race conditions from stale responses
    const latestRequestId = useRef<number>(0);

    const refreshSession = async() => {
        // Generate a unique request ID for this invocation
        const currentRequestId = ++latestRequestId.current;
        
        try {
            const sessionResult = await AuthenticationEndpoint.CheckSession();

            console.log('[SessionContext] refreshSession result:', sessionResult);

            // F-06: Only update state if this is the most recent request
            if (currentRequestId !== latestRequestId.current) {
                console.log('[SessionContext] Stale request, ignoring');
                return;  // This request is stale, ignore its results
            }

            if(sessionResult && sessionResult.errors.length == 0) {
                console.log('[SessionContext] Session valid, data:', sessionResult.data);
                const userResult = sessionResult.data ? sessionResult.data : null;
                setUser(userResult);
            } else {
                if ((sessionResult as any).errors) {
                    console.warn("Session check failed: ", (sessionResult as any).errors);
                }
            }
        } catch (error) {
            // Network or unexpected error
            // F-06: Only update state if this is the most recent request
            if (currentRequestId === latestRequestId.current) {
                setUser(null);
                console.error("Failed to check session:", error);
            }
        } finally {
            // F-06: Only update loading state if this is the most recent request
            if (currentRequestId === latestRequestId.current) {
                setLoading(false);
            }
        }
    };

    const refreshSessionFromEffect = useEffectEvent(() => {
        void refreshSession();
    });

    useEffect(() => {
        refreshSessionFromEffect();
    }, []);

    const hasUser = user !== null;
    const sessionExpiresIn = user?.session_expires_in;

    useEffect(() => {
        if (!hasUser) {
            return;
        }

        let timeoutId: number | undefined;
        let retryIntervalId: number | undefined;
        const expiresAt = typeof sessionExpiresIn === 'number' && Number.isFinite(sessionExpiresIn)
            ? Date.now() + Math.max(0, sessionExpiresIn) * 1000
            : undefined;

        const recheckSession = () => {
            refreshSessionFromEffect();
        };

        const scheduleExpirationCheck = () => {
            if (expiresAt === undefined) {
                return;
            }

            const remaining = expiresAt - Date.now();
            if (remaining > 0) {
                timeoutId = window.setTimeout(scheduleExpirationCheck, Math.min(remaining, MAX_TIMER_DELAY_MS));
                return;
            }

            recheckSession();
            retryIntervalId = window.setInterval(recheckSession, SESSION_RETRY_INTERVAL_MS);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && expiresAt !== undefined && Date.now() >= expiresAt) {
                recheckSession();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        scheduleExpirationCheck();

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
            if (retryIntervalId !== undefined) {
                window.clearInterval(retryIntervalId);
            }
        };
    }, [hasUser, sessionExpiresIn]);

    return (
        <SessionContext.Provider value={{user, setUser, loading, isLoggingOut, setIsLoggingOut, refreshSession}}>
            {children}
        </SessionContext.Provider>
    );
};

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};